import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Graphics } from '@inlet/react-pixi';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { QueueModal as PhoneQueueModal } from 'components/powerCallFlow/components/modals/queue';
import ChatQueueModal from 'components/powerChatFlow/containers/modals/queue';
import { QueueModal as EmailQueueModal } from 'components/powerEmailFlow/components/modals/queue';
import { useFindQueue } from 'components/powerFlowShared/hooks/useFindQueue';
import { usePixiBadge } from 'components/powerFlowShared/hooks/usePixiBadge';
import { QueueModal as MessengerQueueModal } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeQueue/components/queueModal';
import { flowNodeColor, FlowType } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
const translatePayload = (node) => {
    const { queue_timeout } = node.payload;
    return queue_timeout
        ? [
            {
                id: uuid(),
                connection: queue_timeout.timeout_node_id || uuid(),
                label: 'Timeout',
            },
        ]
        : [];
};
const updateConnection = (node, _connection, connectionId) => {
    const payload = Object.assign({}, node.payload);
    if (payload.queue_timeout) {
        payload.queue_timeout = Object.assign(Object.assign({}, payload.queue_timeout), { timeout_node_id: connectionId });
    }
    return Object.assign(Object.assign({}, node), { payload });
};
const settingsLabel = {
    announcement_attributes: 'announce queue position',
    queue_timeout: 'timeout',
    wait_music_v2: 'custom wait music',
    callbackInfo: 'callback',
};
const createQueueNode = (props) => function QueueNode({ node, context }) {
    var _a;
    const { withSettingsText, defaultQueueName } = props || {};
    const { extraData } = context;
    const { payload } = node;
    const queue = useFindQueue(payload.queue, extraData);
    const { text, settings } = useMemo(() => {
        var _a;
        if (!withSettingsText) {
            return { text: '', settings: [] };
        }
        const settings = Object.entries(settingsLabel).reduce((settings, [key, value]) => {
            const isAnnouncementAttributes = key === 'announcement_attributes';
            const data = payload[key];
            if (isAnnouncementAttributes ? data === null || data === void 0 ? void 0 : data.play_announcement : data) {
                settings.push(value);
            }
            return settings;
        }, []);
        const joinedSettings = settings.join(', ');
        const firstSettingsLetter = (_a = joinedSettings[0]) === null || _a === void 0 ? void 0 : _a.toUpperCase();
        const text = `${firstSettingsLetter}${joinedSettings.slice(1)}`;
        return { text, settings };
    }, [payload, withSettingsText]);
    const drawArchivedBadge = usePixiBadge({
        text: 'Archived',
        variant: 'error',
        offset: { x: 0, y: 28 },
    });
    return (_jsx(TextContent, { children: useMemo(() => {
            var _a, _b, _c;
            return (_jsxs(_Fragment, { children: [_jsxs(TextEllipsis, { children: [_jsx(Text, { weight: "normal", text: "Queue" }), _jsx(Text, { weight: "semibold", text: (_c = (_a = queue === null || queue === void 0 ? void 0 : queue.name) !== null && _a !== void 0 ? _a : (_b = payload.queue) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : defaultQueueName })] }), (queue === null || queue === void 0 ? void 0 : queue.is_archived) && _jsx(Graphics, { draw: drawArchivedBadge }), withSettingsText && (_jsxs(TextBreak, Object.assign({ offset: { y: (queue === null || queue === void 0 ? void 0 : queue.is_archived) ? 38 : 5, x: 0 } }, { children: [_jsx(Text, { weight: "normal", text: "Settings" }), _jsx(Text, { weight: "semibold", text: settings.length > 0 ? text : 'None' })] })))] }));
        }, [
            defaultQueueName,
            drawArchivedBadge,
            (_a = payload.queue) === null || _a === void 0 ? void 0 : _a.label,
            queue,
            settings.length,
            text,
            withSettingsText,
        ]) }));
};
const getQueueModalForFlow = (type) => {
    switch (type) {
        case FlowType.FLOW_TYPE_CALL:
            return PhoneQueueModal;
        case FlowType.FLOW_TYPE_EMAIL:
            return EmailQueueModal;
        case FlowType.FLOW_TYPE_CHAT:
            return ChatQueueModal;
        case FlowType.FLOW_TYPE_MESSENGER:
            return MessengerQueueModal;
        default:
            return () => null;
    }
};
export const createQueueNodeAttributes = (type, props) => ({
    iconName: 'users',
    label: 'Queue',
    color: hexStringToNumber(flowNodeColor.green),
    helpText: 'The queue is where you’d like your incoming conversations to automatically end up based on your company’s structure and priorities (e.g. Sales, Support).',
    translatePayload,
    updateConnection,
    NodeContent: createQueueNode(props),
    ActionModal: getQueueModalForFlow(type),
});
export const QueueNodeChatAttributes = createQueueNodeAttributes(FlowType.FLOW_TYPE_CHAT);
export const QueueNodeEmailAttributes = createQueueNodeAttributes(FlowType.FLOW_TYPE_EMAIL, { withSettingsText: true });
export const QueueNodePhoneAttributes = createQueueNodeAttributes(FlowType.FLOW_TYPE_CALL, { withSettingsText: true });
export const QueueNodeMessengerAttributes = createQueueNodeAttributes(FlowType.FLOW_TYPE_MESSENGER, { withSettingsText: true });
